import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, MapPin, BedDouble, Users, Pencil, Trash2, Building2, RefreshCw, ExternalLink, Camera, X, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PropertiesPage() {
  const { user, API } = useAuth();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [form, setForm] = useState({
    name: "", address: "", city: "", owner_id: "", description: "",
    bedrooms: 1, bathrooms: 1, max_guests: 2, pm_fee_percentage: 20,
    price_per_night: 0, airbnb_url: "", booking_url: "", other_ota_urls: "", ical_url: ""
  });

  const isAdmin = user?.role === "admin";

  const fetchData = useCallback(() => {
    const fetches = [axios.get(`${API}/properties`)];
    if (isAdmin) fetches.push(axios.get(`${API}/users/owners`));
    Promise.all(fetches)
      .then(([p, o]) => { setProperties(p.data); if (o) setOwners(o.data); })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [API, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const emptyForm = {
    name: "", address: "", city: "", owner_id: "", description: "",
    bedrooms: 1, bathrooms: 1, max_guests: 2, pm_fee_percentage: 20,
    price_per_night: 0, airbnb_url: "", booking_url: "", other_ota_urls: "", ical_url: ""
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, address: p.address, city: p.city, owner_id: p.owner_id,
      description: p.description || "", bedrooms: p.bedrooms, bathrooms: p.bathrooms,
      max_guests: p.max_guests, pm_fee_percentage: p.pm_fee_percentage ?? 20,
      price_per_night: p.price_per_night || 0, airbnb_url: p.airbnb_url || "",
      booking_url: p.booking_url || "", other_ota_urls: p.other_ota_urls || "",
      ical_url: p.ical_url || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const res = await axios.put(`${API}/properties/${editing.id}`, form);
        setProperties(prev => prev.map(p => p.id === editing.id ? res.data : p));
        toast.success("Proprietà aggiornata");
      } else {
        const res = await axios.post(`${API}/properties`, form);
        setProperties(prev => [...prev, res.data]);
        toast.success("Proprietà aggiunta");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa proprietà? Verranno eliminate anche le prenotazioni associate.")) return;
    try {
      await axios.delete(`${API}/properties/${id}`);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success("Proprietà eliminata");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleSyncICal = async (propId) => {
    setSyncing(propId);
    try {
      const res = await axios.post(`${API}/ical/sync/${propId}`);
      toast.success(`Sincronizzazione completata: ${res.data.imported} importate, ${res.data.skipped} già presenti`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella sincronizzazione iCal");
    } finally {
      setSyncing(null);
    }
  };

  const handlePhotoUpload = async (propId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(propId);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/properties/${propId}/photos`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProperties(prev => prev.map(p => p.id === propId ? { ...p, photos: [...(p.photos || []), res.data.url] } : p));
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento foto");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoDelete = async (propId, photoUrl) => {
    try {
      await axios.delete(`${API}/properties/${propId}/photos`, { params: { photo_url: photoUrl } });
      setProperties(prev => prev.map(p => p.id === propId ? { ...p, photos: (p.photos || []).filter(u => u !== photoUrl) } : p));
      toast.success("Foto eliminata");
    } catch { toast.error("Errore nell'eliminazione foto"); }
  };

  const getPropertyImage = (p) => {
    if (p.photos?.length > 0) return `${BACKEND_URL}${p.photos[0]}`;
    if (p.image_url) return p.image_url;
    return "https://images.unsplash.com/photo-1658248709373-e1e4a79bc192?q=85&w=800&auto=format&fit=crop";
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="properties-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Proprietà</h1>
          <p className="text-stone-500 mt-1 text-sm">{properties.length} proprietà gestite</p>
        </div>
        {isAdmin && (
          <Button onClick={openAdd} data-testid="add-property-button" className="bg-stone-900 hover:bg-stone-800 text-white">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi
          </Button>
        )}
      </div>

      {properties.length === 0 ? (
        <Card className="border border-stone-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 text-stone-300 mx-auto mb-4" strokeWidth={1} />
            <p className="text-stone-500">Nessuna proprietà trovata</p>
            {isAdmin && <Button onClick={openAdd} className="mt-4 bg-stone-900 text-white">Aggiungi la prima proprietà</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((p) => (
            <Card key={p.id} data-testid={`property-card-${p.id}`} className="border border-stone-200 shadow-sm hover:shadow-md transition-all duration-200 group">
              <div className="h-40 bg-stone-100 relative overflow-hidden">
                <img src={getPropertyImage(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1658248709373-e1e4a79bc192?q=85&w=800&auto=format&fit=crop"; }} />
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-white" onClick={() => openEdit(p)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-red-50 text-red-600" onClick={() => handleDelete(p.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                {p.price_per_night > 0 && (
                  <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-sm text-xs font-mono font-semibold text-stone-900">
                    €{p.price_per_night}/notte
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium text-stone-900 text-sm mb-1">{p.name}</h3>
                <div className="flex items-center gap-1 text-stone-500 text-xs mb-2">
                  <MapPin className="h-3 w-3" /> {p.city} · {p.address}
                </div>
                {p.description && <p className="text-xs text-stone-400 mb-2 line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-4 text-xs text-stone-500 mb-2">
                  <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {p.bedrooms}</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {p.max_guests} ospiti</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {p.airbnb_url && <a href={p.airbnb_url} target="_blank" rel="noopener noreferrer"><Badge variant="outline" className="text-[10px] cursor-pointer hover:bg-stone-50">Airbnb <ExternalLink className="h-2.5 w-2.5 ml-1 inline" /></Badge></a>}
                  {p.booking_url && <a href={p.booking_url} target="_blank" rel="noopener noreferrer"><Badge variant="outline" className="text-[10px] cursor-pointer hover:bg-stone-50">Booking <ExternalLink className="h-2.5 w-2.5 ml-1 inline" /></Badge></a>}
                  {p.ical_url && isAdmin && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleSyncICal(p.id)} disabled={syncing === p.id} data-testid={`sync-ical-${p.id}`}>
                      <RefreshCw className={`h-3 w-3 mr-1 ${syncing === p.id ? "animate-spin" : ""}`} />
                      {syncing === p.id ? "Sync..." : "iCal"}
                    </Button>
                  )}
                </div>
                {isAdmin && <p className="text-[11px] text-stone-400 mt-2 font-mono">{p.owner_name} · PM {p.pm_fee_percentage ?? 20}%</p>}

                {isAdmin && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handlePhotoUpload(p.id, e)} />
                        <div className="flex items-center gap-1 text-[10px] text-stone-400 hover:text-stone-600 transition-colors">
                          {uploadingPhoto === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                          <span>Aggiungi foto</span>
                        </div>
                      </label>
                      {p.photos?.length > 0 && <span className="text-[10px] text-stone-300">{p.photos.length} foto</span>}
                    </div>
                    {p.photos?.length > 0 && (
                      <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
                        {p.photos.map((url, i) => (
                          <div key={i} className="relative shrink-0 w-12 h-12 rounded-sm overflow-hidden group/photo">
                            <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => handlePhotoDelete(p.id, url)} className="absolute inset-0 bg-black/50 opacity-0 group-hover/photo:opacity-100 transition-opacity flex items-center justify-center">
                              <X className="h-3 w-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="property-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Modifica Proprietà" : "Nuova Proprietà"}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">{editing ? "Modifica i dettagli della proprietà" : "Inserisci i dettagli della nuova proprietà"}</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="generale" className="mt-2">
            <TabsList className="grid w-full grid-cols-3 h-9">
              <TabsTrigger value="generale" className="text-xs">Generale</TabsTrigger>
              <TabsTrigger value="dettagli" className="text-xs">Dettagli</TabsTrigger>
              <TabsTrigger value="ota" className="text-xs">OTA & iCal</TabsTrigger>
            </TabsList>

            <TabsContent value="generale" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-600">Nome</Label>
                  <Input data-testid="property-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome proprietà" className="mt-1 bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Città</Label>
                  <Input data-testid="property-city-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Città" className="mt-1 bg-white" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Indirizzo</Label>
                <Input data-testid="property-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Indirizzo completo" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Proprietario</Label>
                <Select value={form.owner_id} onValueChange={v => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger data-testid="property-owner-select" className="mt-1 bg-white"><SelectValue placeholder="Seleziona proprietario" /></SelectTrigger>
                  <SelectContent>
                    {owners.map(o => <SelectItem key={o.id} value={o.id}>{o.name} ({o.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Descrizione</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descrizione della proprietà per il portale pubblico..." className="mt-1 bg-white min-h-[80px]" />
              </div>
            </TabsContent>

            <TabsContent value="dettagli" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-stone-600">Camere</Label>
                  <Input type="number" min={1} value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: parseInt(e.target.value) || 1 })} className="mt-1 bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Bagni</Label>
                  <Input type="number" min={1} value={form.bathrooms} onChange={e => setForm({ ...form, bathrooms: parseInt(e.target.value) || 1 })} className="mt-1 bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Max ospiti</Label>
                  <Input type="number" min={1} value={form.max_guests} onChange={e => setForm({ ...form, max_guests: parseInt(e.target.value) || 1 })} className="mt-1 bg-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-stone-600">Prezzo per notte (€)</Label>
                  <Input data-testid="property-price-input" type="number" step="0.01" min={0} value={form.price_per_night} onChange={e => setForm({ ...form, price_per_night: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
                  <p className="text-[10px] text-stone-400 mt-1">Per le prenotazioni dirette</p>
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Commissione PM (%)</Label>
                  <Input data-testid="property-pm-fee-input" type="number" step="0.5" min={0} max={100} value={form.pm_fee_percentage} onChange={e => setForm({ ...form, pm_fee_percentage: parseFloat(e.target.value) || 0 })} className="mt-1 bg-white" />
                  <p className="text-[10px] text-stone-400 mt-1">Sul netto della prenotazione</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ota" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-stone-600">Link Airbnb</Label>
                <Input value={form.airbnb_url} onChange={e => setForm({ ...form, airbnb_url: e.target.value })} placeholder="https://airbnb.com/rooms/..." className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Link Booking.com</Label>
                <Input value={form.booking_url} onChange={e => setForm({ ...form, booking_url: e.target.value })} placeholder="https://booking.com/hotel/..." className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Altri link OTA</Label>
                <Input value={form.other_ota_urls} onChange={e => setForm({ ...form, other_ota_urls: e.target.value })} placeholder="Link separati da virgola" className="mt-1 bg-white" />
              </div>
              <div>
                <Label className="text-xs text-stone-600">URL Calendario iCal</Label>
                <Input data-testid="property-ical-input" value={form.ical_url} onChange={e => setForm({ ...form, ical_url: e.target.value })} placeholder="https://ical.airbnb.com/..." className="mt-1 bg-white" />
                <p className="text-[10px] text-stone-400 mt-1">Per sincronizzare automaticamente le prenotazioni da Airbnb/Booking</p>
              </div>
            </TabsContent>
          </Tabs>

          <Button onClick={handleSave} data-testid="property-save-button" className="w-full bg-stone-900 hover:bg-stone-800 text-white mt-4">
            {editing ? "Salva Modifiche" : "Aggiungi Proprietà"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
